import { backendApi } from "./backendApi.rtkq";
import { type SerializedEditorState } from "lexical";

// Types for transcription data
export interface TranscriptionData {
  id: string;
  userId: string;
  fileName: string;
  storagePath: string;
  transcription: string;
  caseId?: string;
  editorState?: SerializedEditorState | string; // string for TipTap HTML
  editorType?: "lexical" | "tiptap";
  metadata: {
    duration: number;
    fileSize: number;
    chunksProcessed: number;
    language: string;
    model: string;
  };
  processing: {
    status: "processing" | "completed" | "failed";
    startedAt: string;
    completedAt?: string;
    processingTimeMs?: number;
    error?: string;
    errorCode?: string;
  };
  summary?: {
    text: string;
    html: {
      og: string;
      edit: string;
    };
    title: string;
    tags: string[];
    summaryStatus?: "processing" | "completed" | "failed";
    summaryUpdatedAt?: string;
    summaryError?: string;
    editorState?: SerializedEditorState;
    usage: {
      inputTokens: number;
      outputTokens: number;
      totalTokens: number;
    };
    parameters: {
      model: string;
    };
    processingTime: number;
  };
  questionsFromAi?: Array<{
    q: string;
    a: string;
  }>;
  isDeleted?: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Parameters for queries
export interface GetTranscriptionsParams {
  userId: string;
}

export interface GetTranscriptionByIdParams {
  userId: string;
  transcriptionId: string;
}

export interface GetTranscriptionsByStatusParams {
  userId: string;
  status: "processing" | "completed" | "failed";
}

// Parameters for delete operations
export interface DeleteTranscriptionParams {
  userId: string;
  transcriptionId: string;
  deleteType: "soft" | "hard";
}

// Parameters for restore operations
export interface RestoreTranscriptionParams {
  userId: string;
  transcriptionId: string;
}

export interface UpdateTranscriptionEditorStateParams {
  userId: string;
  transcriptionId: string;
  editorState: SerializedEditorState;
}

export interface UpdateTranscriptionTipTapContentParams {
  userId: string;
  transcriptionId: string;
  htmlContent: string;
}

export interface UpdateSummaryHtmlEditParams {
  userId: string;
  transcriptionId: string;
  htmlContent: string;
}

export const transcriptionsApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all transcriptions for a user (excluding soft-deleted)
    getTranscriptions: builder.query<TranscriptionData[], GetTranscriptionsParams>({
      query: ({ userId }) => ({
        url: "/transcriptions",
        params: { userId, excludeDeleted: true },
      }),
      transformResponse: (response: any) => {
        const transcriptions = response.transcriptions || response || [];
        return transcriptions;
      },
      providesTags: (result, _error, { userId }) =>
        result
          ? [
              { type: "Transcriptions", id: `user-${userId}` },
              { type: "Documents", id: `user-${userId}` },
            ]
          : [],
    }),

    // Get transcription by ID
    getTranscriptionById: builder.query<TranscriptionData | null, GetTranscriptionByIdParams>({
      query: ({ transcriptionId }) => `/transcriptions/${transcriptionId}`,
      transformResponse: (response: any) => {
        if (!response) return null;
        return response;
      },
      providesTags: (result, _error, { userId, transcriptionId }) =>
        result
          ? [
              { type: "Transcriptions", id: `${userId}-${transcriptionId}` },
              { type: "Documents", id: `user-${userId}` },
            ]
          : [],
    }),

    // Get transcriptions by processing status (excluding soft-deleted)
    getTranscriptionsByStatus: builder.query<TranscriptionData[], GetTranscriptionsByStatusParams>({
      query: ({ userId, status }) => ({
        url: "/transcriptions",
        params: { userId, status, excludeDeleted: true },
      }),
      transformResponse: (response: any) => {
        const transcriptions = response.transcriptions || response || [];
        return transcriptions;
      },
      providesTags: (result, _error, { userId, status }) =>
        result
          ? [
              { type: "Transcriptions", id: `user-${userId}-status-${status}` },
              { type: "Documents", id: `user-${userId}` },
            ]
          : [],
    }),

    // Get completed transcriptions only
    getCompletedTranscriptions: builder.query<TranscriptionData[], GetTranscriptionsParams>({
      query: ({ userId }) => ({
        url: "/transcriptions",
        params: { userId, status: "completed" },
      }),
      transformResponse: (response: any) => {
        const transcriptions = response.transcriptions || response || [];
        return transcriptions;
      },
      providesTags: (result, _error, { userId }) =>
        result
          ? [
              { type: "Transcriptions", id: `user-${userId}-completed` },
              { type: "Documents", id: `user-${userId}` },
            ]
          : [],
    }),

    // Get all transcriptions for a specific user by userId (excluding soft-deleted)
    getAllUserTranscriptionByUserId: builder.query<TranscriptionData[], GetTranscriptionsParams>({
      query: ({ userId }) => ({
        url: "/transcriptions",
        params: { userId, excludeDeleted: true },
      }),
      transformResponse: (response: any) => {
        const transcriptions = response.transcriptions || response || [];
        return transcriptions;
      },
      providesTags: (result, _error, { userId }) =>
        result
          ? [
              { type: "Transcriptions", id: `user-${userId}-all` },
              { type: "Documents", id: `user-${userId}` },
            ]
          : [],
    }),

    // Delete transcription (soft or hard delete)
    deleteTranscription: builder.mutation<{ success: boolean }, DeleteTranscriptionParams>({
      query: ({ transcriptionId, deleteType }) => ({
        url: `/transcriptions/${transcriptionId}`,
        method: "DELETE",
        params: { deleteType },
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (result, _error, { userId }) =>
        result
          ? [
              { type: "Transcriptions", id: `user-${userId}` },
              { type: "Transcriptions", id: `user-${userId}-all` },
              { type: "Documents", id: `user-${userId}` },
            ]
          : [],
    }),

    // Restore soft-deleted transcription
    restoreTranscription: builder.mutation<{ success: boolean }, RestoreTranscriptionParams>({
      query: ({ transcriptionId }) => ({
        url: `/transcriptions/${transcriptionId}/restore`,
        method: "POST",
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (result, _error, { userId }) =>
        result
          ? [
              { type: "Transcriptions", id: `user-${userId}` },
              { type: "Transcriptions", id: `user-${userId}-all` },
              { type: "Documents", id: `user-${userId}` },
            ]
          : [],
    }),

    // Update transcription editor state
    updateTranscriptionEditorState: builder.mutation<{ success: boolean }, UpdateTranscriptionEditorStateParams>({
      query: ({ transcriptionId, editorState }) => ({
        url: `/transcriptions/${transcriptionId}`,
        method: "PUT",
        body: { editorState },
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (_result, _error, { userId, transcriptionId }) => [
        { type: "Transcriptions", id: `user-${userId}` },
        { type: "Transcriptions", id: `transcription-${transcriptionId}` },
      ],
    }),

    // Update summary editor state
    updateSummaryEditorState: builder.mutation<{ success: boolean }, UpdateSummaryHtmlEditParams>({
      query: ({ transcriptionId, htmlContent }) => ({
        url: `/transcriptions/${transcriptionId}`,
        method: "PUT",
        body: { summaryHtmlEdit: htmlContent },
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (_result, _error, { userId, transcriptionId }) => [
        { type: "Transcriptions", id: `user-${userId}` },
        { type: "Transcriptions", id: `transcription-${transcriptionId}` },
      ],
    }),

    // Update transcription TipTap HTML content
    updateTranscriptionTipTapContent: builder.mutation<{ success: boolean }, UpdateTranscriptionTipTapContentParams>({
      query: ({ transcriptionId, htmlContent }) => ({
        url: `/transcriptions/${transcriptionId}`,
        method: "PUT",
        body: { editorState: htmlContent, editorType: "tiptap" },
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (_result, _error, { userId, transcriptionId }) => [
        { type: "Transcriptions", id: `user-${userId}` },
        { type: "Transcriptions", id: `${userId}-${transcriptionId}` },
      ],
    }),
  }),
});

// Questions-related types
export interface QuestionItem {
  q: string;
  a: string;
}

export interface GenerateQuestionsRequest {
  transcriptionId: string;
}

export interface GenerateQuestionsResponse {
  success: boolean;
  data?: {
    questions: QuestionItem[];
  };
  message?: string;
}

export interface UpdateQuestionAnswerRequest {
  transcriptionId: string;
  questionIndex: number;
  answer: string;
}

export const {
  useGetTranscriptionsQuery,
  useGetTranscriptionByIdQuery,
  useGetTranscriptionsByStatusQuery,
  useGetCompletedTranscriptionsQuery,
  useGetAllUserTranscriptionByUserIdQuery,
  useDeleteTranscriptionMutation,
  useRestoreTranscriptionMutation,
  useUpdateTranscriptionEditorStateMutation,
  useUpdateSummaryEditorStateMutation,
  useUpdateTranscriptionTipTapContentMutation,
} = transcriptionsApi;
